<template>
  <div class="clip-editor">
    <div class="clip-editor__header">
      <h2 class="clip-editor__title">拖动下拉框识别相似音乐</h2>
    </div>

    <div class="clip-editor__body" :class="{ 'is-audio-only': !isVideo }">
      <div v-if="isVideo" class="clip-editor__video">
        <video
          ref="video"
          class="clip-editor__video-el"
          :src="videoSrc"
          playsinline
          preload="metadata"
          @click="togglePlay"
          @play="onVideoPlay"
          @pause="onVideoPause"
        />
      </div>

      <div class="clip-editor__wave-panel">
        <div class="clip-editor__wave-row">
          <button
            type="button"
            class="clip-editor__play"
            :title="playing ? '暂停' : '播放'"
            @click="togglePlay"
          >
            <i :class="playing ? 'el-icon-video-pause' : 'el-icon-video-play'" />
          </button>

          <div class="clip-editor__wave-wrap">
            <div
              v-if="duration > 0"
              class="clip-editor__region-label"
              :style="{ left: startPct + '%' }"
            >
              {{ formatClock(regionStart) }}
            </div>
            <div
              v-if="duration > 0"
              class="clip-editor__region-label"
              :style="{ left: endPct + '%' }"
            >
              {{ formatClock(regionEnd) }}
            </div>
            <div ref="wave" class="clip-editor__wave" />
          </div>
        </div>

        <div v-if="infoBanner" class="clip-editor__banner">
          {{ infoBanner }}
        </div>
      </div>
    </div>

    <div class="clip-editor__footer">
      <button type="button" class="clip-editor__search-btn" @click="onSearchClip">
        <i class="el-icon-search"></i>
        找片段相似音乐
      </button>
      <div class="clip-editor__time-range">
        <span>输入时间范围</span>
        <el-input
          v-model="startInput"
          class="clip-editor__time-input"
          size="small"
          @change="onStartInputChange"
        />
        <span class="clip-editor__time-sep">—</span>
        <el-input
          v-model="endInput"
          class="clip-editor__time-input"
          size="small"
          @change="onEndInputChange"
        />
      </div>
    </div>

    <audio ref="audio" class="clip-editor__audio" preload="auto" />
  </div>
</template>

<script>
import WaveSurfer from 'wavesurfer.js'
import RegionsPlugin from 'wavesurfer.js/dist/plugin/wavesurfer.regions.min.js'

const DEFAULT_LEN = 30
const MAX_LEN = 60
const MIN_LEN = 1

export default {
  name: 'MediaClipEditor',
  props: {
    stat: {
      type: Object,
      required: true
    },
    waveData: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      wavesurfer: null,
      region: null,
      playing: false,
      duration: 0,
      regionStart: 0,
      regionEnd: DEFAULT_LEN,
      startInput: '00:00',
      endInput: '00:30',
      seekingPlay: false,
      syncingMedia: false,
      updatingRegion: false
    }
  },
  computed: {
    isVideo() {
      return (this.stat.file_type || '').toLowerCase() === 'video'
    },
    videoSrc() {
      return this.stat.file_path || ''
    },
    audioSrc() {
      return this.stat.wav_path || this.stat.file_path || ''
    },
    infoBanner() {
      const zone = this.stat.wav_zone
      // 区间数组用于恢复选区，不当做 banner 文案
      if (Array.isArray(zone)) return ''
      if (zone == null) return ''
      return String(zone).trim()
    },
    startPct() {
      if (!this.duration) return 0
      return (this.regionStart / this.duration) * 100
    },
    endPct() {
      if (!this.duration) return 0
      return (this.regionEnd / this.duration) * 100
    }
  },
  watch: {
    audioSrc() {
      this.$nextTick(() => this.initWave())
    },
    waveData: {
      handler() {
        this.$nextTick(() => this.initWave())
      },
      deep: true
    }
  },
  mounted() {
    this.$nextTick(() => this.initWave())
  },
  beforeDestroy() {
    this.destroyWave()
  },
  methods: {
    parseWavZone(zone) {
      let start
      let end
      if (Array.isArray(zone) && zone.length >= 2) {
        start = Number(zone[0])
        end = Number(zone[1])
      } else if (typeof zone === 'string') {
        const text = zone.trim()
        const m = text.match(/^(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)$/)
        if (!m) return null
        start = Number(m[1])
        end = Number(m[2])
      } else {
        return null
      }
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
      return { start, end }
    },
    formatClock(sec) {
      const s = Math.max(0, Math.floor(sec || 0))
      const m = Math.floor(s / 60)
      const r = s % 60
      return `${m}:${String(r).padStart(2, '0')}`
    },
    formatInput(sec) {
      const s = Math.max(0, Math.floor(sec || 0))
      const m = Math.floor(s / 60)
      const r = s % 60
      return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
    },
    parseInput(str) {
      const text = String(str || '').trim()
      const m = text.match(/^(\d{1,2}):(\d{1,2})$/)
      if (!m) return null
      const mm = Number(m[1])
      const ss = Number(m[2])
      if (ss >= 60) return null
      return mm * 60 + ss
    },
    syncInputsFromRegion() {
      this.startInput = this.formatInput(this.regionStart)
      this.endInput = this.formatInput(this.regionEnd)
    },
    clampRegion(start, end) {
      let s = Math.max(0, start)
      let e = Math.max(s + MIN_LEN, end)
      if (this.duration > 0) {
        e = Math.min(e, this.duration)
        s = Math.min(s, Math.max(0, this.duration - MIN_LEN))
        if (e - s > MAX_LEN) {
          s = e - MAX_LEN
          if (s < 0) {
            s = 0
            e = Math.min(MAX_LEN, this.duration)
          }
        }
        if (e - s < MIN_LEN) {
          e = Math.min(s + MIN_LEN, this.duration)
        }
      } else if (e - s > MAX_LEN) {
        e = s + MAX_LEN
      }
      return { start: s, end: e }
    },
    applyRegionTimes(start, end, updateWs = true) {
      const clamped = this.clampRegion(start, end)
      this.regionStart = clamped.start
      this.regionEnd = clamped.end
      this.syncInputsFromRegion()
      if (updateWs && this.region) {
        this.updatingRegion = true
        this.region.update({
          start: this.regionStart,
          end: this.regionEnd
        })
        this.$nextTick(() => {
          this.updatingRegion = false
        })
      }
    },
    destroyWave() {
      if (this.wavesurfer) {
        try {
          this.wavesurfer.destroy()
        } catch (e) {
          // ignore
        }
        this.wavesurfer = null
        this.region = null
      }
      this.playing = false
    },
    initWave() {
      if (!this.$refs.wave || !this.audioSrc) return
      this.destroyWave()

      const audio = this.$refs.audio
      this.wavesurfer = WaveSurfer.create({
        container: this.$refs.wave,
        backend: 'MediaElement',
        media: audio,
        waveColor: '#d0d0d0',
        progressColor: '#e52a0d',
        cursorColor: '#e52a0d',
        cursorWidth: 2,
        barWidth: 2,
        barGap: 1,
        height: 48,
        normalize: true,
        interact: true,
        plugins: [
          RegionsPlugin.create({
            dragSelection: false
          })
        ]
      })

      this.wavesurfer.on('ready', () => {
        this.duration = this.wavesurfer.getDuration() || 0
        const zone = this.parseWavZone(this.stat.wav_zone)
        if (zone) {
          this.createSelection(zone.start, zone.end)
          return
        }
        const len = Math.min(DEFAULT_LEN, this.duration || DEFAULT_LEN)
        const start = this.duration > 0 ? Math.max(0, (this.duration - len) / 2) : 0
        const end = start + (len > 0 ? len : DEFAULT_LEN)
        this.createSelection(start, end)
      })

      this.wavesurfer.on('play', () => {
        this.playing = true
        this.syncVideoPlay(true)
      })
      this.wavesurfer.on('pause', () => {
        this.playing = false
        this.syncVideoPlay(false)
      })
      this.wavesurfer.on('finish', () => {
        this.playing = false
        this.syncVideoPlay(false)
      })
      this.wavesurfer.on('audioprocess', () => {
        this.syncVideoTime()
      })
      this.wavesurfer.on('seek', () => {
        this.syncVideoTime(true)
        this.onWaveSeekPlay()
      })
      this.wavesurfer.on('region-click', (region, e) => {
        this.onRegionClickPlay(e)
      })

      const peaks =
        this.waveData && this.waveData.length > 0 ? this.waveData : undefined
      this.wavesurfer.load(this.audioSrc, peaks)
    },
    createSelection(start, end) {
      if (!this.wavesurfer) return
      this.wavesurfer.clearRegions()
      const clamped = this.clampRegion(start, end)
      this.regionStart = clamped.start
      this.regionEnd = clamped.end
      this.syncInputsFromRegion()
      this.region = this.wavesurfer.addRegion({
        start: this.regionStart,
        end: this.regionEnd,
        color: 'rgba(229, 42, 13, 0.22)',
        drag: true,
        resize: true,
        handleStyle: {
          left: {
            width: '3px',
            backgroundColor: '#1a1a1a'
          },
          right: {
            width: '3px',
            backgroundColor: '#1a1a1a'
          }
        }
      })

      this.region.on('update', () => {
        if (this.updatingRegion) return
        const len = this.region.end - this.region.start
        if (len > MAX_LEN) {
          const endMoved =
            Math.abs(this.region.end - this.regionEnd) >=
            Math.abs(this.region.start - this.regionStart)
          if (endMoved) {
            this.applyRegionTimes(this.region.end - MAX_LEN, this.region.end)
          } else {
            this.applyRegionTimes(this.region.start, this.region.start + MAX_LEN)
          }
          return
        }
        if (len < MIN_LEN) {
          this.applyRegionTimes(this.region.start, this.region.start + MIN_LEN)
          return
        }
        this.regionStart = this.region.start
        this.regionEnd = this.region.end
        this.syncInputsFromRegion()
      })
    },
    syncVideoTime(force) {
      if (!this.isVideo || !this.$refs.video || this.syncingMedia) return
      const t = this.wavesurfer ? this.wavesurfer.getCurrentTime() : 0
      const video = this.$refs.video
      if (force || Math.abs(video.currentTime - t) > 0.25) {
        this.syncingMedia = true
        try {
          video.currentTime = t
        } catch (e) {
          // ignore
        }
        this.syncingMedia = false
      }
    },
    syncVideoPlay(shouldPlay) {
      if (!this.isVideo || !this.$refs.video) return
      const video = this.$refs.video
      this.syncVideoTime(true)
      if (shouldPlay) {
        const p = video.play()
        if (p && p.catch) p.catch(() => {})
      } else {
        video.pause()
      }
    },
    stopGlobalMusic() {
      this.$store.dispatch('music/musicStop').catch(() => {})
      this.$store.commit('music/UPDATE_MUSIC_CONTROLS_VISIBLE', false)
    },
    emitPlayStart() {
      this.stopGlobalMusic()
      this.$emit('play-start')
    },
    pause() {
      if (this.wavesurfer && this.wavesurfer.isPlaying()) {
        this.wavesurfer.pause()
      }
      if (this.isVideo && this.$refs.video) {
        try {
          this.$refs.video.pause()
        } catch (e) {
          // ignore
        }
      }
    },
    playFromProgress(progress) {
      if (!this.wavesurfer) return
      const duration = this.wavesurfer.getDuration() || this.duration || 0
      if (duration <= 0) return
      const start = Math.max(0, Math.min(duration, progress * duration))
      this.emitPlayStart()
      this.seekingPlay = true
      this.wavesurfer.play(start)
      setTimeout(() => {
        this.seekingPlay = false
      }, 100)
    },
    onWaveSeekPlay() {
      if (!this.wavesurfer || this.seekingPlay || this.updatingRegion || this.syncingMedia) return
      this.emitPlayStart()
      if (this.wavesurfer.isPlaying()) return
      this.seekingPlay = true
      this.$nextTick(() => {
        if (this.wavesurfer && !this.wavesurfer.isPlaying()) {
          this.wavesurfer.play()
        }
        setTimeout(() => {
          this.seekingPlay = false
        }, 80)
      })
    },
    onRegionClickPlay(e) {
      if (!this.wavesurfer || !this.$refs.wave || this.seekingPlay) return
      const rect = this.$refs.wave.getBoundingClientRect()
      if (!rect.width) return
      const progress = (e.clientX - rect.left) / rect.width
      this.playFromProgress(progress)
    },
    togglePlay() {
      if (!this.wavesurfer) return
      if (!this.wavesurfer.isPlaying()) {
        this.emitPlayStart()
      }
      this.wavesurfer.playPause()
    },
    onVideoPlay() {
      if (!this.wavesurfer || this.wavesurfer.isPlaying()) return
      this.emitPlayStart()
      this.syncingMedia = true
      this.wavesurfer.play(this.$refs.video.currentTime)
      this.syncingMedia = false
    },
    onVideoPause() {
      if (!this.wavesurfer || !this.wavesurfer.isPlaying()) return
      this.wavesurfer.pause()
    },
    onStartInputChange() {
      const s = this.parseInput(this.startInput)
      if (s == null) {
        this.syncInputsFromRegion()
        this.$message.warning('时间格式应为 mm:ss')
        return
      }
      this.applyRegionTimes(s, this.regionEnd)
    },
    onEndInputChange() {
      const e = this.parseInput(this.endInput)
      if (e == null) {
        this.syncInputsFromRegion()
        this.$message.warning('时间格式应为 mm:ss')
        return
      }
      this.applyRegionTimes(this.regionStart, e)
    },
    buildRange() {
      const start = Math.floor(this.regionStart)
      const end = Math.floor(this.regionEnd)
      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return { ok: false, message: '时间范围无效' }
      }
      if (start < 0 || end < 0) {
        return { ok: false, message: '时间范围不能为负数' }
      }
      if (start >= end) {
        return { ok: false, message: '结束时间必须大于开始时间' }
      }
      if (this.duration > 0 && end > Math.floor(this.duration)) {
        return { ok: false, message: '时间范围超出音频时长' }
      }
      const range = `${start}-${end}`
      if (!/^\d+-\d+$/.test(range)) {
        return { ok: false, message: '时间范围格式不正确，应为 start-end' }
      }
      return { ok: true, range }
    },
    onSearchClip() {
      const checked = this.buildRange()
      if (!checked.ok) {
        this.$message.warning(checked.message)
        return
      }
      this.$emit('search-clip', { range: checked.range })
    }
  }
}
</script>

<style lang="scss" scoped>
.clip-editor {
  width: 100%;
  background: #fff;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  padding: 12px 24px 16px;
  box-sizing: border-box;

  &__header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 8px;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
  }

  &__body {
    display: flex;
    gap: 16px;
    align-items: stretch;

    &.is-audio-only {
      .clip-editor__wave-panel {
        width: 100%;
      }
    }
  }

  &__video {
    width: 200px;
    flex-shrink: 0;
    height: 160px;
    background: #000;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__video-el {
    width: 100%;
    height: 100%;
    object-fit: contain;
    cursor: pointer;
  }

  &__wave-panel {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__wave-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  &__play {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    background: #f0f0f0;
    color: #333;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    outline: none;

    &:hover {
      color: #e52a0d;
      background: rgba(229, 42, 13, 0.08);
    }
  }

  &__wave-wrap {
    position: relative;
    flex: 1;
    min-width: 0;
    padding-top: 14px;
  }

  &__region-label {
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    font-size: 12px;
    color: #333;
    line-height: 1;
    white-space: nowrap;
    pointer-events: none;
    z-index: 2;
  }

  &__wave {
    width: 100%;
  }

  &__banner {
    margin-top: 8px;
    padding: 6px 12px;
    background: #fdeceb;
    color: #e52a0d;
    font-size: 13px;
    border-radius: 4px;
    line-height: 1.4;
  }

  &__footer {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-height: 40px;
    margin-top: 12px;
  }

  &__time-range {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #666;
  }

  &__time-input {
    width: 72px;
  }

  &__time-sep {
    color: #999;
  }

  &__search-btn {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 200px;
    height: 40px;
    padding: 0 24px;
    border: none;
    border-radius: 20px;
    background: #e52a0d;
    color: #fff;
    font-size: 14px;
    cursor: pointer;
    outline: none;
    flex-shrink: 0;

    &:hover {
      background: #c9240b;
    }
  }

  &__audio {
    display: none;
  }
}
</style>
